import { Box, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import { RegistrationRow } from '~/components/RegistrationRow/RegistrationRows';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import usePrimaryImage from '~/hooks/image/usePrimaryImage';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import useDeleteDataset from '~/hooks/useDeleteDataset';
import {
    useActivateRegistration,
    useRegistrationState,
} from '~/hooks/registration/useRegistrationStore';

function resolveSecondarySeriesUid(
    reg: RigidRegistration,
    patient: {
        images: ReadonlyArray<{
            frameOfReferenceUid: string;
            seriesUid: string;
        }>;
    }
): string | undefined {
    return patient.images.find(
        (img) => img.frameOfReferenceUid === reg.targetFrameOfReferenceUid
    )?.seriesUid;
}

export default function RegistrationList() {
    const intl = useIntl();
    const patient = useSelectedPatient();

    const { primaryImage } = usePrimaryImage();
    const registrationState = useRegistrationState();
    const activateRegistration = useActivateRegistration();
    const { deleteRegistration } = useDeleteDataset();
    const allRegistrations = useMemo(
        () => patient?.registrations ?? [],
        [patient?.registrations]
    );

    const [deleteTarget, setDeleteTarget] = useState<RigidRegistration | null>(
        null
    );

    const handleConfirmDelete = () => {
        if (deleteTarget) {
            deleteRegistration(deleteTarget);
        }
        setDeleteTarget(null);
    };

    if (allRegistrations.length === 0) {
        return null;
    }

    return (
        <>
            <Box>
                <Box px={2} pt={1.5} pb={1}>
                    <Typography fontSize={12} color="primary" fontWeight={500}>
                        <FormattedMessage id="registrations" />
                    </Typography>
                </Box>
                <Box display="flex" flexDirection="column" px={1.5} pb={1}>
                    {allRegistrations.map((reg) => {
                        const secondarySeriesUid = patient
                            ? resolveSecondarySeriesUid(reg, patient)
                            : undefined;
                        // A registration is activated from its source image
                        // only: fused the other way round, a manual alignment
                        // would edit it backwards.
                        const canActivate =
                            !!primaryImage &&
                            !!secondarySeriesUid &&
                            reg.editableFrom(primaryImage.frameOfReferenceUid);
                        const isSelected =
                            registrationState.selectedSopInstanceUid ===
                            reg.dicomUid;
                        return (
                            <RegistrationRow
                                key={reg.dicomUid}
                                label={reg.contentLabel || reg.dicomUid}
                                kind={reg.registrationKind}
                                approvalStatus={reg.approvalStatus}
                                selected={isSelected}
                                onClick={
                                    canActivate
                                        ? () =>
                                              activateRegistration(
                                                  reg.dicomUid,
                                                  secondarySeriesUid!
                                              )
                                        : undefined
                                }
                                details={{
                                    registration: reg,
                                    onDelete: !reg.isApproved
                                        ? () => setDeleteTarget(reg)
                                        : undefined,
                                }}
                            />
                        );
                    })}
                </Box>
            </Box>

            <ConfirmationDialog
                open={deleteTarget !== null}
                title={intl.formatMessage({ id: 'deleteRegistration' })}
                message={intl.formatMessage({
                    id: 'deleteRegistrationConfirm',
                })}
                onConfirm={handleConfirmDelete}
                onCancel={() => setDeleteTarget(null)}
                mode="error"
            />
        </>
    );
}
